import { removeDuplicateDims, removeCoincidentDims } from "building-generation/utils/dimensionRows";
import { makeFoundationUtilityPlanDimensions } from "building-generation/utils/foundation";
import type { Dimension } from "building-types/dimensions/dimensionTypes";
import type { AutoDimensionRuntimeHandle } from "building-types/handles/docEntityHandleTypes";
import {
  foundationDimensionCategoryReplaceMap,
  utilityDimensionCategoryReplaceMap,
} from "building-types/views/sheetGeometryCategory";
import { makeDimension } from "doc/dimensions";
import { makeSheetGeometryFromDimensions } from "doc/plans/planDimensions";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import { PlanGeometryBuilder } from "doc/plans/planGeometrySource";
import type { PlanGeometryGeneratorResult } from "doc/plans/planGeometrySourceTypes";
import { filterInteriorDimensionsToReportingUnit } from "./interiorDimensionReportingUnitFilter";
import { getReportingUnitRoomIds } from "./reportingUnitRooms";

export function makeSheetGeometryFromFoundationDimensionsByLevel({
  building,
  levelId,
  dimensionPrecision,
  planRotationRads,
  selectedReportingUnitLabelId,
}: PlanGenerationContext) {
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);

  let interiorDimensions = building?.foundationInteriorDimensions[levelId] || [];
  // Drop exterior foundation dimensions when a reporting unit is selected; keep the interior ones
  // anchored entirely within the unit.
  const exteriorDimensions = reportingUnitRoomIds ? [] : building?.foundationExteriorDimensions[levelId] || [];

  if (reportingUnitRoomIds) {
    interiorDimensions = filterInteriorDimensionsToReportingUnit(interiorDimensions, building, reportingUnitRoomIds);
  }

  const displayInteriorDims: Dimension[] = [];
  interiorDimensions.forEach((dim) =>
    makeDimension(displayInteriorDims, {
      ...dim,
      dimensionPrecision,
      handle: dim.docEntityHandle as AutoDimensionRuntimeHandle | undefined,
    }),
  );
  const displayExteriorDims: Dimension[] = [];
  exteriorDimensions.forEach((dim) =>
    makeDimension(displayExteriorDims, {
      ...dim,
      dimensionPrecision,
      handle: dim.docEntityHandle as AutoDimensionRuntimeHandle | undefined,
    }),
  );

  const interiorDimensionsNoDups = removeDuplicateDims(removeCoincidentDims(displayInteriorDims), displayExteriorDims);

  const dimensions = [...displayExteriorDims, ...interiorDimensionsNoDups];

  const dimensionPlanGeometries = makeSheetGeometryFromDimensions({
    handle: dimensions.map((dim) => dim.handle),
    interiorDimensionType: building.settings.planset.interiorDimensionType,
    dimensions,
    planRotationRads,
  });

  const builder = new PlanGeometryBuilder();
  builder.addAllFrom(dimensionPlanGeometries, foundationDimensionCategoryReplaceMap);
  return builder;
}

export async function makePlanGeometryFromUtilityDimensions({
  building,
  levelId,
  planRotationRads,
}: PlanGenerationContext): Promise<PlanGeometryGeneratorResult> {
  const dimensions: Dimension[] = await makeFoundationUtilityPlanDimensions(building, levelId);
  const dimensionPlanGeometries = makeSheetGeometryFromDimensions({
    handle: building.levels[levelId].handle,
    interiorDimensionType: building.settings.planset.interiorDimensionType,
    dimensions,
    planRotationRads,
  });

  const builder = new PlanGeometryBuilder();
  builder.addAllFrom(dimensionPlanGeometries, utilityDimensionCategoryReplaceMap);
  return builder;
}
